import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  InputBase,
  IconButton,
  Tooltip,
  Button,
  Typography,
  GlobalStyles
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import {
  FIND_HIGHLIGHT,
  FIND_CURRENT_HIGHLIGHT,
  MAX_FIND_MATCHES,
  collectTextSegments,
  findMatches,
  createMatchRange,
  sanitizeFindInput,
  buildFindHighlightCss
} from '../../utils/findReplace';

const SEARCH_DELAY_MS = 150;
const REFRESH_DELAY_MS = 200;
const OBSERVER_OPTIONS = { childList: true, subtree: true, characterData: true };

const MODES = [
  { id: 'find', label: 'Rechercher', Icon: SearchIcon },
  { id: 'replace', label: 'Remplacer', Icon: FindReplaceIcon }
];

const plural = (count, word) => `${count} ${word}${count > 1 ? 's' : ''}`;
const replacedLabel = (count) => `${plural(count, 'occurrence')} remplacée${count > 1 ? 's' : ''}`;

// Rectangle d'une occurrence dans la fenêtre principale (le contenu des iframes TinyMCE est décalé)
const getMatchRect = (match) => {
  try {
    const rect = match.range.getBoundingClientRect();
    const win = match.range.startContainer.ownerDocument?.defaultView;
    const frame = win && win !== window ? win.frameElement : null;
    if (!frame) return rect;
    const frameRect = frame.getBoundingClientRect();
    return {
      top: frameRect.top + rect.top,
      bottom: frameRect.top + rect.bottom,
      left: frameRect.left + rect.left,
      right: frameRect.left + rect.right,
      width: rect.width,
      height: rect.height
    };
  } catch (error) {
    return null;
  }
};

const isRectVisible = (rect) => Boolean(rect) && (rect.width > 0 || rect.height > 0);

// Zone `data-find-clip` (bloc coupé entre deux pages de l'aperçu, rendu dans chaque page) :
// l'occurrence n'appartient qu'au fragment où commence sa première ligne
const isRangeInsideBox = (range, box) => {
  const first = Array.from(range.getClientRects()).find((rect) => rect.width > 0 || rect.height > 0);
  if (!first) return false;
  const middle = (first.top + first.bottom) / 2;
  return middle >= box.top && middle <= box.bottom;
};

// Hauteur masquée par le panneau en haut de la zone de défilement
const getTopOffset = (panel) => (panel ? panel.offsetTop + panel.offsetHeight : 0) + 12;

// Première occurrence visible ou située sous le haut de la zone affichée
const pickInitialIndex = (matches, container) => {
  if (!container) return 0;
  const top = container.getBoundingClientRect().top;
  for (let i = 0; i < matches.length; i += 1) {
    const rect = getMatchRect(matches[i]);
    if (isRectVisible(rect) && rect.bottom >= top) return i;
  }
  return 0;
};

const scrollMatchIntoView = (match, container, topOffset) => {
  if (!match || !container) return;
  try {
    const win = match.range.startContainer.ownerDocument?.defaultView;
    const frame = win && win !== window ? win.frameElement : null;
    if (frame) {
      // Éditeur plus haut que sa zone visible : défiler d'abord à l'intérieur de l'iframe
      const inner = match.range.getBoundingClientRect();
      if (inner.top < 0 || inner.bottom > win.innerHeight) {
        win.scrollBy(0, inner.top - win.innerHeight / 2);
      }
    }

    const rect = getMatchRect(match);
    if (!isRectVisible(rect)) return;
    const box = container.getBoundingClientRect();
    const fitsVertically = rect.top >= box.top + topOffset && rect.bottom <= box.bottom - 32;
    const fitsHorizontally = rect.left >= box.left + 16 && rect.right <= box.right - 16;
    if (fitsVertically && fitsHorizontally) return;

    const visibleHeight = box.height - topOffset;
    container.scrollTo({
      top: fitsVertically
        ? container.scrollTop
        : container.scrollTop + (rect.top + rect.bottom) / 2 - (box.top + topOffset + visibleHeight / 2),
      left: fitsHorizontally
        ? container.scrollLeft
        : container.scrollLeft + (rect.left + rect.right) / 2 - (box.left + box.width / 2),
      behavior: 'smooth'
    });
  } catch (error) {
    // L'occurrence a été retirée du document entre-temps
  }
};

// Texte sélectionné dans le document, repris comme recherche (comme Word)
const getSelectedText = (container) => {
  const selection = window.getSelection ? window.getSelection() : null;
  if (!container || !selection || selection.isCollapsed || !selection.rangeCount) return '';
  if (!container.contains(selection.anchorNode) || !container.contains(selection.focusNode)) return '';
  const text = selection.toString().trim();
  if (!text || text.length > 120 || /[\r\n]/.test(text)) return '';
  return text;
};

/**
 * Panneau flottant Rechercher / Remplacer du document (Ctrl+F / Ctrl+H).
 *
 * Une instance par vue : `onModeChange` reçoit le clic sur l'onglet de l'autre mode,
 * `sharedStateRef` conserve la saisie d'une instance à l'autre.
 *
 * Les zones explorées sont les éléments `[data-find-scope]` du conteneur :
 * - `title` / `content` : titre et contenu affichés d'une rubrique (`data-find-rubric`)
 * - `editor` : rubrique ouverte dans TinyMCE (corps fourni par `getEditorBody`)
 * - `static` : texte généré (introduction, conclusion), consultable mais non remplaçable
 * Avec `data-find-clip`, seules comptent les occurrences visibles dans la zone (fragment de page).
 *
 * Les occurrences sont surlignées via l'API CSS Custom Highlight : le DOM rendu
 * par React n'est jamais modifié pour la recherche.
 */
function FindReplacePanel({
  request,
  onClose,
  onModeChange,
  sharedStateRef,
  containerRef,
  getEditorBody,
  getReplaceBlocker,
  onReplaceOne,
  onReplaceAll,
  refreshKey,
  isDark,
  highlightDark = isDark,
  offsetTop = 12,
  hidden
}) {
  const open = Boolean(request);
  const [mode, setMode] = useState('find');
  const [query, setQuery] = useState('');
  const [replacement, setReplacement] = useState('');
  const [matchCase, setMatchCase] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [matchCount, setMatchCount] = useState(0);
  const [capped, setCapped] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(-1);
  // { severity: 'success' | 'warning', text }
  const [status, setStatus] = useState(null);
  // { count, skipped } pendant la confirmation de « Tout remplacer »
  const [confirmAll, setConfirmAll] = useState(null);
  const [busy, setBusy] = useState(false);

  const panelRef = useRef(null);
  const searchInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const matchesRef = useRef([]);
  const currentIndexRef = useRef(-1);
  const lastSearchKeyRef = useRef(null);
  const highlightWindowsRef = useRef(new Set());
  const refreshTimerRef = useRef(null);
  const observerRef = useRef(null);

  const options = useMemo(() => ({ matchCase, wholeWord }), [matchCase, wholeWord]);
  const cleanQuery = sanitizeFindInput(query);
  const searchKey = `${cleanQuery}\u0001${matchCase}\u0001${wholeWord}`;

  const clearHighlights = useCallback(() => {
    highlightWindowsRef.current.forEach((win) => {
      try {
        win.CSS?.highlights?.delete(FIND_HIGHLIGHT);
        win.CSS?.highlights?.delete(FIND_CURRENT_HIGHLIGHT);
      } catch (error) {
        // Fenêtre d'un éditeur déjà fermé
      }
    });
    highlightWindowsRef.current.clear();
  }, []);

  const paintHighlights = useCallback(() => {
    clearHighlights();

    // Chaque iframe TinyMCE a son propre registre de surlignages
    const byWindow = new Map();
    matchesRef.current.forEach((match, index) => {
      const win = match.range.startContainer.ownerDocument?.defaultView;
      if (!win) return;
      if (!byWindow.has(win)) byWindow.set(win, { all: [], current: null });
      const entry = byWindow.get(win);
      entry.all.push(match.range);
      if (index === currentIndexRef.current) entry.current = match.range;
    });

    byWindow.forEach((entry, win) => {
      try {
        const registry = win.CSS?.highlights;
        const HighlightClass = win.Highlight;
        if (!registry || typeof HighlightClass !== 'function') return;

        const all = new HighlightClass();
        entry.all.forEach((range) => all.add(range));
        registry.set(FIND_HIGHLIGHT, all);

        if (entry.current) {
          const current = new HighlightClass(entry.current);
          current.priority = 1;
          registry.set(FIND_CURRENT_HIGHLIGHT, current);
        }
        highlightWindowsRef.current.add(win);
      } catch (error) {
        // Navigateur sans surlignage personnalisé : la navigation reste possible
      }
    });
  }, [clearHighlights]);

  const collectMatches = useCallback(() => {
    const container = containerRef.current;
    if (!container || !cleanQuery) return { matches: [], capped: false };

    const matches = [];
    const scopes = Array.from(container.querySelectorAll('[data-find-scope]'));

    for (let s = 0; s < scopes.length; s += 1) {
      const element = scopes[s];
      const scope = element.getAttribute('data-find-scope');
      const rubricId = element.getAttribute('data-find-rubric');
      const root = scope === 'editor' ? getEditorBody?.(rubricId) : element;
      if (!root) continue;

      // Le contenu de l'iframe n'est pas couvert par l'observateur du conteneur
      if (scope === 'editor' && observerRef.current) {
        observerRef.current.observe(root, OBSERVER_OPTIONS);
      }

      const { text, segments } = collectTextSegments(root);
      const found = findMatches(text, cleanQuery, options);
      const clipBox = element.hasAttribute('data-find-clip') ? element.getBoundingClientRect() : null;

      for (let indexInScope = 0; indexInScope < found.length; indexInScope += 1) {
        const range = createMatchRange(segments, found[indexInScope]);
        if (!range || (clipBox && !isRangeInsideBox(range, clipBox))) continue;
        if (matches.length >= MAX_FIND_MATCHES) return { matches, capped: true };
        matches.push({ scope, rubricId, indexInScope, scopeCount: found.length, range });
      }
    }

    return { matches, capped: false };
  }, [containerRef, getEditorBody, cleanQuery, options]);

  // reason : 'query' (nouvelle recherche), 'replace' (après un remplacement), 'refresh' (le document a changé)
  const runSearch = useCallback(({ reason = 'refresh', index = null } = {}) => {
    clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = null;

    // Une actualisation qui précède la recherche de la saisie en cours vaut nouvelle recherche
    const effectiveReason = lastSearchKeyRef.current !== searchKey && index === null ? 'query' : reason;
    const { matches, capped: isCapped } = collectMatches();
    matchesRef.current = matches;
    lastSearchKeyRef.current = searchKey;

    let next = -1;
    if (matches.length) {
      if (index !== null) {
        next = ((index % matches.length) + matches.length) % matches.length;
      } else if (effectiveReason === 'query') {
        next = pickInitialIndex(matches, containerRef.current);
      } else {
        next = Math.min(Math.max(currentIndexRef.current, 0), matches.length - 1);
      }
    }

    currentIndexRef.current = next;
    setMatchCount(matches.length);
    setCapped(isCapped);
    setCurrentIndex(next);
    paintHighlights();

    if (effectiveReason !== 'refresh' && next >= 0) {
      scrollMatchIntoView(matches[next], containerRef.current, getTopOffset(panelRef.current));
    }
  }, [collectMatches, paintHighlights, containerRef, searchKey]);

  const runSearchRef = useRef(runSearch);
  runSearchRef.current = runSearch;

  const scheduleRefresh = useCallback(() => {
    clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      runSearchRef.current({ reason: 'refresh' });
    }, REFRESH_DELAY_MS);
  }, []);

  // Avant d'agir, s'assurer que les occurrences correspondent à la saisie et au document actuels
  const ensureFreshMatches = () => {
    if (lastSearchKeyRef.current !== searchKey) {
      runSearchRef.current({ reason: 'query' });
      return false;
    }
    if (refreshTimerRef.current) runSearchRef.current({ reason: 'refresh' });
    return true;
  };

  // Suivre les modifications du document (sauvegardes, autres utilisateurs, saisie dans l'éditeur)
  useEffect(() => {
    const container = containerRef.current;
    if (!open || !container || typeof MutationObserver === 'undefined') return undefined;

    const observer = new MutationObserver(scheduleRefresh);
    observer.observe(container, OBSERVER_OPTIONS);
    observerRef.current = observer;

    return () => {
      observer.disconnect();
      observerRef.current = null;
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    };
  }, [open, containerRef, scheduleRefresh]);

  // Nouvelle recherche quand la saisie ou les options changent
  useEffect(() => {
    if (!open) return undefined;
    setStatus(null);
    setConfirmAll(null);
    const timer = setTimeout(() => {
      if (lastSearchKeyRef.current !== searchKey) runSearchRef.current({ reason: 'query' });
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [open, searchKey]);

  // Un éditeur TinyMCE vient de s'initialiser : son contenu est désormais consultable
  useEffect(() => {
    if (open) scheduleRefresh();
  }, [open, refreshKey, scheduleRefresh]);

  // Ouverture (ou nouvelle demande Ctrl+F / Ctrl+H alors que le panneau est ouvert)
  useEffect(() => {
    if (!request) return undefined;
    setMode(request.mode);
    setConfirmAll(null);
    const saved = sharedStateRef?.current;
    if (saved) {
      setQuery(saved.query);
      setReplacement(saved.replacement);
      setMatchCase(saved.matchCase);
      setWholeWord(saved.wholeWord);
    }
    const selectedText = getSelectedText(containerRef.current);
    if (selectedText) setQuery(selectedText);

    const timer = setTimeout(() => {
      const input = searchInputRef.current;
      if (!input) return;
      input.focus();
      input.select();
    }, 0);
    return () => clearTimeout(timer);
  }, [request, containerRef, sharedStateRef]);

  // Doit suivre l'effet d'ouverture : la saisie partagée y est lue avant d'être réécrite
  useEffect(() => {
    if (open && sharedStateRef) {
      sharedStateRef.current = { query, replacement, matchCase, wholeWord };
    }
  }, [open, sharedStateRef, query, replacement, matchCase, wholeWord]);

  // Fermeture : retirer tous les surlignages (la saisie est conservée pour la prochaine ouverture)
  useEffect(() => {
    if (open) return;
    matchesRef.current = [];
    currentIndexRef.current = -1;
    lastSearchKeyRef.current = null;
    clearHighlights();
    setMatchCount(0);
    setCapped(false);
    setCurrentIndex(-1);
    setStatus(null);
    setConfirmAll(null);
    setBusy(false);
  }, [open, clearHighlights]);

  useEffect(() => () => {
    clearTimeout(refreshTimerRef.current);
    clearHighlights();
  }, [clearHighlights]);

  const goTo = (delta) => {
    if (!ensureFreshMatches()) return;
    const count = matchesRef.current.length;
    if (!count) return;

    const from = currentIndexRef.current;
    const next = from < 0 ? (delta > 0 ? 0 : count - 1) : (from + delta + count) % count;
    currentIndexRef.current = next;
    setCurrentIndex(next);
    setStatus(null);
    setConfirmAll(null);
    paintHighlights();
    scrollMatchIntoView(matchesRef.current[next], containerRef.current, getTopOffset(panelRef.current));
  };

  const switchMode = (nextMode) => {
    if (nextMode === mode) return;
    if (onModeChange) {
      onModeChange(nextMode);
      return;
    }
    setMode(nextMode);
    setConfirmAll(null);
    setTimeout(() => {
      const input = nextMode === 'replace' && cleanQuery ? replaceInputRef.current : searchInputRef.current;
      input?.focus();
    }, 0);
  };

  const handleReplaceOne = () => {
    if (busy || !ensureFreshMatches()) return;
    const index = currentIndexRef.current;
    const match = matchesRef.current[index];
    if (!match) return;

    const blocker = getReplaceBlocker(match);
    if (blocker) {
      setStatus({ severity: 'warning', text: `Occurrence non modifiable : ${blocker}` });
      return;
    }

    const cleanReplacement = sanitizeFindInput(replacement);
    const result = onReplaceOne(match, cleanQuery, options, cleanReplacement);
    setConfirmAll(null);
    setStatus(result?.ok
      ? { severity: 'success', text: result.message || 'Occurrence remplacée.' }
      : { severity: 'warning', text: result?.message || "Le remplacement n'a pas pu être effectué." });

    // Si le texte de remplacement contient lui-même la recherche, ne pas retomber dessus
    const advance = result?.ok ? findMatches(cleanReplacement, cleanQuery, options).length : 0;
    setBusy(true);
    // Après le rendu des rubriques modifiées : se placer sur l'occurrence suivante
    setTimeout(() => {
      runSearchRef.current({ reason: 'replace', index: index + advance });
      setBusy(false);
    }, 0);
  };

  const handleReplaceAllClick = () => {
    if (busy) return;
    ensureFreshMatches();
    const matches = matchesRef.current;
    if (!matches.length) {
      setStatus({ severity: 'warning', text: 'Aucune occurrence à remplacer.' });
      return;
    }

    const replaceable = matches.filter((match) => !getReplaceBlocker(match)).length;
    if (!replaceable) {
      setStatus({
        severity: 'warning',
        text: 'Aucune de ces occurrences ne peut être remplacée (texte automatique ou rubrique verrouillée).'
      });
      return;
    }

    setStatus(null);
    setConfirmAll({ count: replaceable, skipped: matches.length - replaceable });
  };

  const handleConfirmReplaceAll = () => {
    if (!confirmAll || busy) return;
    const { skipped } = confirmAll;
    setConfirmAll(null);

    const result = onReplaceAll(cleanQuery, options, sanitizeFindInput(replacement)) || {};
    if (!result.replaced) {
      setStatus({
        severity: 'warning',
        text: result.message || "Aucune occurrence n'a été remplacée : le document a changé, relancez la recherche."
      });
    } else {
      const parts = [`${replacedLabel(result.replaced)} dans ${plural(result.rubrics, 'rubrique')}.`];
      if (skipped) parts.push(`${plural(skipped, 'occurrence')} non modifiable${skipped > 1 ? 's' : ''} ignorée${skipped > 1 ? 's' : ''}.`);
      if (result.editorRubrics) parts.push('Pensez à enregistrer la rubrique en cours de modification.');
      setStatus({ severity: 'success', text: parts.join(' ') });
    }

    setBusy(true);
    setTimeout(() => {
      runSearchRef.current({ reason: 'refresh' });
      setBusy(false);
    }, 0);
  };

  const handlePanelKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (confirmAll) setConfirmAll(null);
      else onClose();
    } else if (event.key === 'F3') {
      event.preventDefault();
      goTo(event.shiftKey ? -1 : 1);
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      goTo(event.shiftKey ? -1 : 1);
    }
  };

  const handleReplaceKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleReplaceOne();
    }
  };

  if (!open) return null;

  const accent = isDark ? '#4fc3f7' : '#0288d1';
  const accentHover = isDark ? '#81d4fa' : '#01579b';
  const accentContrast = isDark ? '#0d1b26' : '#ffffff';
  const textColor = isDark ? '#ecf0f1' : '#1f2937';
  const mutedColor = isDark ? '#95a5a6' : '#64748b';
  const fieldBorder = isDark ? '#3d5a73' : '#cfd8e3';
  const fieldBackground = isDark ? '#1a2530' : '#f8fafc';
  const errorColor = isDark ? '#ff6b6b' : '#d32f2f';
  const successColor = isDark ? '#66bb6a' : '#2e7d32';
  const warningColor = isDark ? '#ffb74d' : '#b26a00';

  const searched = Boolean(cleanQuery) && lastSearchKeyRef.current === searchKey;
  const noResult = searched && matchCount === 0;
  const counterLabel = !searched
    ? ''
    : (matchCount ? `${currentIndex + 1} sur ${matchCount}${capped ? '+' : ''}` : 'Aucun résultat');

  const currentMatch = currentIndex >= 0 ? matchesRef.current[currentIndex] : null;
  const currentBlocker = mode === 'replace' && currentMatch ? getReplaceBlocker(currentMatch) : null;

  const fieldSx = (hasError) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    height: 38,
    pl: 1.25,
    pr: 0.5,
    borderRadius: '8px',
    border: `1px solid ${hasError ? errorColor : fieldBorder}`,
    backgroundColor: fieldBackground,
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:focus-within': {
      borderColor: hasError ? errorColor : accent,
      boxShadow: `0 0 0 3px ${hasError ? errorColor : accent}26`
    }
  });

  const inputSx = {
    flex: 1,
    minWidth: 0,
    fontSize: '0.9rem',
    color: textColor,
    '& input::placeholder': { color: mutedColor, opacity: 1 }
  };

  const iconButtonSx = {
    width: 28,
    height: 28,
    color: mutedColor,
    '&:hover': {
      color: accent,
      backgroundColor: isDark ? 'rgba(79, 195, 247, 0.12)' : 'rgba(2, 136, 209, 0.08)'
    },
    '&.Mui-disabled': { color: isDark ? '#4a5a68' : '#cbd5e1' }
  };

  const renderToggle = ({ active, onClick, label, title }) => (
    <Tooltip title={title} arrow>
      <Button
        size="small"
        onClick={onClick}
        aria-pressed={active}
        sx={{
          minWidth: 0,
          px: 1.25,
          py: 0.25,
          borderRadius: '999px',
          fontSize: '0.75rem',
          fontWeight: 600,
          lineHeight: 1.6,
          textTransform: 'none',
          border: `1px solid ${active ? accent : fieldBorder}`,
          color: active ? accent : mutedColor,
          backgroundColor: active ? (isDark ? 'rgba(79, 195, 247, 0.14)' : 'rgba(2, 136, 209, 0.08)') : 'transparent',
          '&:hover': {
            borderColor: accent,
            color: accent,
            backgroundColor: isDark ? 'rgba(79, 195, 247, 0.18)' : 'rgba(2, 136, 209, 0.12)'
          }
        }}
      >
        {label}
      </Button>
    </Tooltip>
  );

  let footer = null;
  if (status) {
    footer = { color: status.severity === 'success' ? successColor : warningColor, text: status.text };
  } else if (currentBlocker) {
    footer = { color: mutedColor, text: `Occurrence non modifiable : ${currentBlocker}` };
  }

  return (
    <>
      <GlobalStyles styles={buildFindHighlightCss(highlightDark)} />
      <Paper
        ref={panelRef}
        elevation={0}
        role="search"
        aria-label="Rechercher dans le document"
        onKeyDown={handlePanelKeyDown}
        sx={{
          position: 'absolute',
          top: offsetTop,
          right: 24,
          zIndex: 20,
          width: 420,
          maxWidth: 'calc(100% - 48px)',
          p: 1.25,
          display: hidden ? 'none' : 'block',
          borderRadius: '12px',
          backgroundColor: isDark ? '#22303e' : '#ffffff',
          backgroundImage: 'none',
          color: textColor,
          border: `1px solid ${isDark ? '#3d5a73' : 'rgba(2, 136, 209, 0.25)'}`,
          boxShadow: isDark
            ? '0 12px 32px rgba(0, 0, 0, 0.5)'
            : '0 12px 32px rgba(15, 35, 70, 0.18)'
        }}
      >
        {/* Onglets Rechercher / Remplacer */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box
            sx={{
              display: 'inline-flex',
              gap: '2px',
              p: '3px',
              borderRadius: '999px',
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(2, 136, 209, 0.08)'
            }}
          >
            {MODES.map(({ id, label, Icon }) => {
              const active = mode === id;
              return (
                <Button
                  key={id}
                  size="small"
                  onClick={() => switchMode(id)}
                  startIcon={<Icon sx={{ fontSize: '1rem !important' }} />}
                  aria-pressed={active}
                  sx={{
                    minWidth: 0,
                    px: 1.5,
                    py: 0.25,
                    borderRadius: '999px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: active ? accentContrast : (isDark ? '#c9d1d9' : '#455a64'),
                    backgroundColor: active ? accent : 'transparent',
                    '&:hover': {
                      backgroundColor: active ? accentHover : (isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(2, 136, 209, 0.12)')
                    }
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </Box>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Fermer (Échap)" arrow>
            <IconButton size="small" onClick={onClose} aria-label="Fermer la recherche" sx={iconButtonSx}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Champ de recherche */}
        <Box sx={fieldSx(noResult)}>
          <SearchIcon sx={{ fontSize: 18, color: mutedColor, flexShrink: 0 }} />
          <InputBase
            inputRef={searchInputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Rechercher dans le document"
            inputProps={{ 'aria-label': 'Texte à rechercher', spellCheck: false }}
            sx={inputSx}
          />
          <Typography
            variant="caption"
            aria-live="polite"
            sx={{
              color: noResult ? errorColor : mutedColor,
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
              px: 0.5
            }}
          >
            {counterLabel}
          </Typography>
          <Tooltip title="Précédent (Maj+Entrée)" arrow>
            <span>
              <IconButton size="small" onClick={() => goTo(-1)} disabled={!matchCount} aria-label="Occurrence précédente" sx={iconButtonSx}>
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Suivant (Entrée)" arrow>
            <span>
              <IconButton size="small" onClick={() => goTo(1)} disabled={!matchCount} aria-label="Occurrence suivante" sx={iconButtonSx}>
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Options de recherche */}
        <Box sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
          {renderToggle({
            active: matchCase,
            onClick: () => setMatchCase((value) => !value),
            label: 'Aa',
            title: 'Respecter la casse'
          })}
          {renderToggle({
            active: wholeWord,
            onClick: () => setWholeWord((value) => !value),
            label: 'Mot entier',
            title: 'Rechercher uniquement des mots entiers'
          })}
        </Box>

        {mode === 'replace' && (
          <>
            <Box sx={{ ...fieldSx(false), mt: 1 }}>
              <FindReplaceIcon sx={{ fontSize: 18, color: mutedColor, flexShrink: 0 }} />
              <InputBase
                inputRef={replaceInputRef}
                value={replacement}
                onChange={(event) => setReplacement(event.target.value)}
                onKeyDown={handleReplaceKeyDown}
                placeholder="Remplacer par"
                inputProps={{ 'aria-label': 'Texte de remplacement', spellCheck: false }}
                sx={inputSx}
              />
            </Box>

            {confirmAll ? (
              <Box
                sx={{
                  mt: 1,
                  p: 1.25,
                  borderRadius: '8px',
                  border: `1px solid ${isDark ? 'rgba(255, 183, 77, 0.4)' : 'rgba(237, 108, 2, 0.35)'}`,
                  backgroundColor: isDark ? 'rgba(255, 183, 77, 0.1)' : 'rgba(237, 108, 2, 0.06)'
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: textColor }}>
                  Remplacer {capped ? 'au moins ' : ''}{plural(confirmAll.count, 'occurrence')} de « {cleanQuery} »
                  par « {sanitizeFindInput(replacement) || '(rien)'} » ?
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: mutedColor }}>
                  {confirmAll.skipped > 0 && `${plural(confirmAll.skipped, 'occurrence')} non modifiable${confirmAll.skipped > 1 ? 's' : ''} ser${confirmAll.skipped > 1 ? 'ont' : 'a'} ignorée${confirmAll.skipped > 1 ? 's' : ''}. `}
                  Les rubriques enregistrées sont modifiées immédiatement ; leurs versions précédentes restent restaurables depuis l'historique.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                  <Button
                    size="small"
                    onClick={() => setConfirmAll(null)}
                    sx={{ textTransform: 'none', color: mutedColor }}
                  >
                    Annuler
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    autoFocus
                    onClick={handleConfirmReplaceAll}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: 'none',
                      backgroundColor: isDark ? '#ff9800' : '#ed6c02',
                      color: '#ffffff',
                      '&:hover': { backgroundColor: isDark ? '#f57c00' : '#c75a00', boxShadow: 'none' }
                    }}
                  >
                    Tout remplacer
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleReplaceOne}
                  disabled={busy || !currentMatch || Boolean(currentBlocker)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: accent,
                    color: accent,
                    '&:hover': {
                      borderColor: accent,
                      backgroundColor: isDark ? 'rgba(79, 195, 247, 0.12)' : 'rgba(2, 136, 209, 0.08)'
                    },
                    '&.Mui-disabled': {
                      borderColor: isDark ? '#3d5a73' : '#e2e8f0',
                      color: isDark ? '#4a5a68' : '#94a3b8'
                    }
                  }}
                >
                  Remplacer
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleReplaceAllClick}
                  disabled={busy || !matchCount}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: 'none',
                    backgroundColor: accent,
                    color: accentContrast,
                    '&:hover': { backgroundColor: accentHover, boxShadow: 'none' },
                    '&.Mui-disabled': {
                      backgroundColor: isDark ? '#2d3f4c' : '#e2e8f0',
                      color: isDark ? '#4a5a68' : '#94a3b8'
                    }
                  }}
                >
                  Tout remplacer
                </Button>
              </Box>
            )}
          </>
        )}

        {footer && (
          <Typography
            variant="caption"
            role="status"
            sx={{ display: 'block', mt: 1, color: footer.color, lineHeight: 1.4 }}
          >
            {footer.text}
          </Typography>
        )}
      </Paper>
    </>
  );
}

export default FindReplacePanel;
